/* 符合条件的汽车的列表 */
import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'
import css from 'styled-jsx/css'
import Link from 'next/link'
import fetch from 'lib/decorated-fetch'
import classnames from 'classnames'
import ScrollLoader from 'Components/scroll-loader'

import getUrl from 'lib/url'
import { sendTeaCommEvent } from 'lib/tea-analyze'

import Image from 'Components/image'
import Loading from 'Components/loading'
import { useCityName } from 'Components/hooks'

import { ContextStore } from './reducer'
import { ISortProps, IOptionProps, IOptionsState, ICarProps } from './interface'

interface Props {
  sort: ISortProps[]
}
interface ICarData {
  /*  汽车总数量 */
  series_count?: number
  /* 汽车列表 */
  series: ICarProps[]
}

export default function CarList({ sort }: Props) {
  // 已选中的条件
  const { state, dispatch } = useContext(ContextStore)
  // 获取当前城市
  const cityName = useCityName() || ''
  /* 懒加载剩余数量 */
  const count = useRef(0)
  /* 懒加载当前第几页 */
  const offset = useRef(0)

  const [loading, setLoading] = useState(true)
  /* 符合条件的汽车列表 */
  const [carData, setCarData] = useState<ICarData>({
    series: [],
    series_count: undefined,
  })

  // }, [state, state, cityName])

  // 处理请求参数，序列化已选择条件
  const getParams = useCallback(() => {
    console.log('getParams', state)
    return Object.entries(state)
      .filter(([, value]) => !!value && value.length > 0)
      .map(([key, value]) => {
        if (!value) {
          return ''
        }
        return `${key}=${value.map(item => item.param).join(',')}`
      })
      .join('&')
  }, [state])
  // 统计用参数
  function getStat() {
    return Object.keys(state).reduce((acc: any, curr: string) => {
      acc[curr] = state[curr].map(item => item.param).join(',')
      return acc
    }, {})
  }
  // 获取汽车类型
  const getCars = useCallback(() => {
    setLoading(true)
    return fetch(
      getUrl(
        `/motor/brand/m/v6/select/series/?city_name=${encodeURIComponent(
          cityName
        )}`
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `offset=${offset.current}&limit=30&is_refresh=${
          offset.current ? 0 : 1
        }&city_name=${encodeURIComponent(cityName)}&${getParams()}`,
      }
    )
  }, [cityName, getParams])

  useEffect(() => {
    offset.current = 0
    if (cityName) {
      getCars()
        .then(data => {
          if (data.status === 'success') {
            count.current = data.data.series_count
            setCarData(data.data)
          }
          setLoading(false)
        })
        .catch(err => {
          setLoading(false)
        })
    }
  }, [cityName, getCars])
  // 懒加载
  function loadMoreHandler() {
    count.current -= 20
    offset.current += 1
    getCars()
      .then(data => {
        setLoading(false)
        if (data.status === 'success') {
          sendTeaCommEvent('load_garage_series', {
            list_item_num: data.data.series.length,
          })
          setCarData({
            series_count: data.data.series_count,
            series: [...carData.series, ...data.data.series],
          })
        }
      })
      .catch(err => {
        setLoading(false)
      })
  }

  function renderList(data: ICarProps, index: number) {
    // console.log(data)
    const imageComponent = css.resolve`
      .img {
        width: 100%;
        height: 90px;
      }
    `
    return (
      <Link
        key={index}
        href={`/auto/series?seriesId=${data.id || data.info?.series_id}`}
        as={`/auto/series/${data.id || data.info?.series_id}`}
      >
        <a
          target="_blank"
          onClick={() => {
            sendTeaCommEvent(
              'click_garage_series_cell',
              Object.assign(
                {
                  car_series_name: data.outter_name || data.info?.series_name,
                  car_series_id: data.id || data.info?.series_id,
                  rank: index,
                },
                getStat()
              )
            )
          }}
        >
          <div className="image-wrapper">
            <Image
              className={classnames(imageComponent.className, 'img')}
              src={data.cover_url || data.info?.image_url}
            />
          </div>
          <p
            className="name line-1"
            title={data.outter_name || data.info?.series_name}
          >
            {data.outter_name || data.info?.series_name}
          </p>
          <p className="price">
            {data.dealer_price || data.info?.dealer_price}
          </p>
          {imageComponent.styles}
          <style jsx>
            {`
              a {
                display: inline-block;
                width: 180px;
                margin: 20px 0 0;
                text-align: center;
                box-sizing: border-box;
              }

              .image-wrapper {
                width: 135px;
                margin: 0 auto;
              }

              .name {
                line-height: 22px;
                margin-bottom: 4px;
                font-size: 16px;
              }
              .name:hover {
                color: #406599;
              }
              .price {
                line-height: 20px;
                color: #ff9100;
                display: inline-block;
                height: 20px;
              }
              .price:hover {
                text-decoration: underline;
              }
            `}
          </style>
        </a>
      </Link>
    )
  }

  function isOptionActive(option: ISortProps, index: number): boolean {
    if (!option) {
      return false
    }
    // 如果没有删选默认显示第一个
    if (!state[option.key] && index === 0) {
      return true
    }
    if (
      state[option.key] &&
      state[option.key].some(item => item.param === option.param)
    ) {
      return true
    }
    return false
  }

  return (
    <React.Fragment>
      <header>
        <span className="active">{carData.series_count}</span> 车系符合条件
        <div className="conditions">
          {sort.map((item: ISortProps, index) => {
            return (
              <span
                key={item.text}
                role="button"
                className={`price-sort ${classnames({
                  active: isOptionActive(item, index),
                })}`}
                onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                  e.stopPropagation()
                  sendTeaCommEvent('click_garage_series_order', {
                    sort_type: item.param,
                  })
                  dispatch({
                    type: 'setSingleOption',
                    payload: {
                      key: item.key,
                      text: item.text,
                      param: item.param,
                    },
                  })
                }}
              >
                {item.text}
              </span>
            )
          })}
        </div>
      </header>
      {carData.series_count === 0 && (
        <div className="empty-tip">抱歉，未找到合适车型</div>
      )}
      <ul className="list">
        <ScrollLoader
          loading={loading}
          end={count.current - 20 >= 0 ? false : true}
          loadMoreHandler={loadMoreHandler}
        >
          {carData.series.map((item: ICarProps, index: number) =>
            renderList(item, index)
          )}
        </ScrollLoader>
        {loading && <Loading />}
      </ul>
      <style jsx>
        {`
          header {
            line-height: 54px;
            margin: 0 20px;
            font-size: 16px;
            border-bottom: 1px solid #ededed;
          }
          .conditions {
            float: right;
          }
          .conditions span {
            padding-left: 24px;
            cursor: pointer;
            transition: color 0.1s ease-in-out;
          }
          .price-sort:hover {
            color: #ff9100;
          }
          .active {
            color: #ff9100;
          }
          .list {
            margin: 0 0 50px 0;
          }
          .empty-tip {
            text-align: center;
            padding-top: 30px;
            font-size: 16px;
            color: #999;
          }
        `}
      </style>
    </React.Fragment>
  )
}
